import React from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import { usePlaylistSongs } from '../../hooks/usePlaylistSongs';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { SMLImage } from '../SMLImage';
import { SMLLink } from '../SMLLink/SMLLink';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { Panel, PanelBody, PanelHead } from '../primitives/Panel';
import styles from './PlaylistSongsList.module.scss';

interface PlaylistSongsListProps {
  playlistId: string;
  lastUpdated?: string | null;
}

const PlaylistSongsListComponent: React.FC<PlaylistSongsListProps> = ({ playlistId, lastUpdated }) => {
  const intl = useIntl();
  const { data, isLoading, error, refetch } = usePlaylistSongs({ playlistId });

  const songs = data?.songs ?? [];
  const total = data?.total ?? 0;
  const hasMore = songs.length < total;

  return (
    <Panel>
      <PanelHead
        title={intl.formatMessage(
          { id: 'app.sml.playlists.songs.title.count', defaultMessage: 'Songs ({count})' },
          { count: total }
        )}
        subtitle={
          lastUpdated !== undefined && lastUpdated !== null
            ? intl.formatMessage(
                { id: 'app.sml.playlists.songs.asOf', defaultMessage: 'as of {date}' },
                {
                  date: new Date(lastUpdated).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  }),
                }
              )
            : undefined
        }
      />
      <PanelBody>
        {isLoading && <LoadingView />}

        {error !== null && !isLoading && (
          <ErrorView
            error={error}
            retry={() => {
              refetch().catch(console.error);
            }}
          />
        )}

        {!isLoading && error === null && songs.length === 0 && (
          <div className={styles.emptyState}>
            {intl.formatMessage({
              id: 'app.sml.playlists.songs.empty',
              defaultMessage: 'No songs in this playlist yet.',
            })}
          </div>
        )}

        {!isLoading &&
          error === null &&
          songs.map(
            (song: {
              id: string;
              position: number;
              name: string;
              artistName: string;
              imageUrl: string | null;
              addedAt: string;
            }) => (
              <SMLLink
                key={`${song.id}-${song.position}`}
                to={SMLRoutes.trackDetails}
                params={{ trackId: song.id }}
                className={styles.songRow}
              >
                <span className={styles.position}>{song.position + 1}</span>
                <SMLImage src={song.imageUrl} alt={song.name} width={36} makeSquareFit className={styles.trackImage} />
                <div className={styles.trackInfo}>
                  <div className={styles.trackName}>{song.name}</div>
                  <div className={styles.artistName}>{song.artistName}</div>
                </div>
              </SMLLink>
            )
          )}

        {hasMore && !isLoading && (
          <div className={styles.loadMore}>
            <Button color="link" size="sm" disabled>
              {intl.formatMessage(
                { id: 'app.sml.playlists.songs.showingOf', defaultMessage: 'Showing {shown} of {total}' },
                { shown: songs.length, total }
              )}
            </Button>
          </div>
        )}
      </PanelBody>
    </Panel>
  );
};

export const PlaylistSongsList = TypedMemo(PlaylistSongsListComponent);
